import "./primaryContactDetailsEditComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../../../store/reducers";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import CommonService from "../../../../../shared/services/common.service";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import FormikInputComponent from "../../../../../shared/components/core/formik-input/FormikInputComponent";
import Grid from "@mui/material/Grid";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import FormikAutoCompleteDropdown
    from "../../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import FromikPhoneNumberComponent
    from "../../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";
import commonService from "../../../../../shared/services/common.service";
import LinkComponent from "../../../../../shared/components/link/LinkComponent";

interface PrimaryContactDetailsEditComponentProps {
    facilityDetails: any
    onUpdateComplete: Function;
    onEditCancel: Function;
    facilityId: number
}

const FacilityPrimaryContactDetailsValidationSchema = Yup.object({
    isSameAsAdmin: Yup.boolean().required("Please select one option"),
    firstName: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string().nullable().required("First Name  is required"),
    }),
    lastName: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string().nullable().required("Last Name  is required"),
    }),
    middleName: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string().nullable()
    }),
    emailId: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string()
            .nullable()
            .email("Invalid Email ID")
            .required("Email ID is required")
            .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Invalid Email ID")
    }),
    mobileNumber: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string()
            .nullable()
            .required("Mobile Number is required")
            .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),

    }),
    faxNumber: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string()
            .nullable()
            .matches(/\d+/, 'only digits allowed')
            .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Fax Number should be 10 digits"),
    }),
    secondaryMobileNumber: Yup.string().nullable().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string()
            .nullable()
            .matches(/\d+/, 'only digits allowed')
            .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits")
    }),
    accountExecutiveId: Yup.mixed()
        .required("Account Executive is required"),

});


interface FacilityPrimaryContactDetailsFormSchema {
    isSameAsAdmin: boolean;
    firstName: string;
    lastName: string;
    middleName: string;
    mobileNumber: string;
    emailId: string;
    secondaryMobileNumber: string;
    faxNumber: string;
    accountExecutiveId: string;
}

const formInitialValues: FacilityPrimaryContactDetailsFormSchema = {
    isSameAsAdmin: true,
    firstName: "",
    lastName: "",
    middleName: "",
    mobileNumber: "",
    emailId: "",
    secondaryMobileNumber: "",
    faxNumber: "",
    accountExecutiveId: "",
}

const PrimaryContactDetailsEditComponent = (props: PrimaryContactDetailsEditComponentProps) => {
    const {facilityDetails, facilityId, onUpdateComplete, onEditCancel} = props;
    const [initialValues, setInitialValues] = useState<FacilityPrimaryContactDetailsFormSchema>(formInitialValues);
    const {accountExecutiveList} = useSelector((state: IRootReducerState) => state.staticData);

    const onSubmit = useCallback((values: any, {
        setSubmitting,
        setErrors
    }: FormikHelpers<any>) => {
        const primaryContactDetails = {...values};
        primaryContactDetails["mobileNumber"] = primaryContactDetails["mobileNumber"] ? primaryContactDetails["mobileNumber"].replace(/[^0-9]/g, '') : "";
        primaryContactDetails["secondaryMobileNumber"] = primaryContactDetails["secondaryMobileNumber"] ? primaryContactDetails["secondaryMobileNumber"].replace(/[^0-9]/g, '') : "";
        primaryContactDetails["faxNumber"] = primaryContactDetails["faxNumber"] ? primaryContactDetails["faxNumber"].replace(/[^0-9]/g, '') : "";
        const payload = {
            primaryContactDetails: primaryContactDetails,
            isSameAsAdmin: primaryContactDetails.isSameAsAdmin,
            accountExecutiveId: primaryContactDetails.accountExecutiveId.id
        };
        if (payload.primaryContactDetails.isSameAsAdmin) {
            delete payload.primaryContactDetails;
        }
        setSubmitting(true);
        CommonService._facility.addPrimaryContactDetails(facilityId, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                const primaryContactDetailsObj = response.data;
                onUpdateComplete(primaryContactDetailsObj);
                CommonService._alert.showToast(response?.message || 'primary Contact details has been added', "success");
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
                CommonService._alert.showToast(error.error || "Something went wrong!", "error")
            })
    }, [facilityId, onUpdateComplete]);

    const handlePopulateAdminDetails = useCallback((setFieldValue: (field: string, value: any, shouldValidate?: (boolean | undefined)) => void) => {
        if (!facilityDetails?.adminId) {
            CommonService._alert.showToast("Admin is not assigned to this facility", "warning");
            return;
        }
        const {
            firstName = '',
            middleName = '',
            lastName = '',
            mobileNumber = '',
            secondaryMobileNumber = '',
            faxNumber = '',
            emailId = ''
        } = facilityDetails?.adminId ? (facilityDetails?.adminDetails || {}) : {};

        setFieldValue('firstName', firstName)
        setFieldValue('lastName', lastName)
        setFieldValue('middleName', middleName)
        setFieldValue('mobileNumber', CommonService.getMaskedMobileNumber(mobileNumber))
        setFieldValue('secondaryMobileNumber', CommonService.getMaskedMobileNumber(secondaryMobileNumber))
        setFieldValue('faxNumber', CommonService.getMaskedMobileNumber(faxNumber))
        setFieldValue('emailId', emailId)
    }, [facilityDetails?.adminDetails, facilityDetails?.adminId])

    useEffect(() => {
        const facilityPrimaryContactDetails = facilityDetails?.primaryContactDetails;
        if (facilityPrimaryContactDetails?.isSameAsAdmin) {
            facilityPrimaryContactDetails.isSameAsAdmin = false;
        }
        if (facilityPrimaryContactDetails) {
            if (facilityPrimaryContactDetails?.isSameAsAdmin) {
                setInitialValues((prev) => {
                    return {
                        ...prev,
                        isSameAsAdmin: facilityPrimaryContactDetails?.isSameAsAdmin || false,
                        accountExecutiveId: facilityPrimaryContactDetails.accountExecutiveDetails
                    }
                })
            } else {
                setInitialValues(() => {
                    return {
                        isSameAsAdmin: facilityPrimaryContactDetails?.isSameAsAdmin || false,
                        firstName: facilityPrimaryContactDetails?.firstName || "",
                        lastName: facilityPrimaryContactDetails?.lastName || "",
                        middleName: facilityPrimaryContactDetails?.middleName || "",
                        mobileNumber: commonService.getMaskedMobileNumber(facilityPrimaryContactDetails.mobileNumber),
                        emailId: facilityPrimaryContactDetails.emailId,
                        secondaryMobileNumber: commonService.getMaskedMobileNumber(facilityPrimaryContactDetails.secondaryMobileNumber),
                        faxNumber: commonService.getMaskedMobileNumber(facilityPrimaryContactDetails?.faxNumber),
                        accountExecutiveId: facilityPrimaryContactDetails.accountExecutiveDetails
                    }
                });
            }
        }
    }, [facilityDetails?.primaryContactDetails])

    return (
        <div className={'primary-contact-details-edit-component details-tab-data-content'}>
            <Formik
                validationSchema={FacilityPrimaryContactDetailsValidationSchema}
                initialValues={initialValues}
                validateOnChange={false}
                validateOnBlur={true}
                enableReinitialize={true}
                validateOnMount={true}
                onSubmit={onSubmit}
            >
                {({isSubmitting, resetForm, dirty, values, isValid, setFieldValue, validateForm, errors}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values]);
                    return (
                        <Form noValidate={true} className={"patient-personal-information-holder"}>
                            <div className="t-form-controls-wrapper">
                                <div className="form-sub-title">Primary Contact Details</div>
                                <div className="d-flex align-items-center mrg-bottom-10">
                                    <div className="redirect-new-link-wrapper mrg-bottom-20">
                                        <div>Fetch details of admin?</div>
                                        <LinkComponent
                                            className={"redirect-link"}
                                            onClick={() => handlePopulateAdminDetails(setFieldValue)}>
                                            Fetch Admin Details
                                        </LinkComponent>
                                    </div>
                                    {/*<div className="mrg-right-30 radio-button-title">Is the admin overseeing this*/}
                                    {/*    role?*/}
                                    {/*</div>*/}
                                    {/*<Field*/}
                                    {/*    name={'isSameAsAdmin'}*/}
                                    {/*>*/}
                                    {/*    {*/}
                                    {/*        (field: FieldProps) => (*/}
                                    {/*            <FormikRadioButtonGroupComponent*/}
                                    {/*                direction={"row"}*/}
                                    {/*                isValueBoolean={true}*/}
                                    {/*                titleKey={'title'}*/}
                                    {/*                valueKey={'code'}*/}
                                    {/*                options={CommonService._staticData.yesNoOptions}*/}
                                    {/*                formikField={field}*/}
                                    {/*            />*/}
                                    {/*        )*/}
                                    {/*    }*/}
                                    {/*</Field>*/}
                                </div>
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'firstName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'First Name'}
                                                            placeholder={'Enter First Name'}
                                                            type={"text"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'middleName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Middle Name'}
                                                            placeholder={'Enter Middle Name'}
                                                            type={"text"}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'lastName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Last Name'}
                                                            placeholder={'Enter Last Name'}
                                                            type={"text"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'emailId'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Email ID'}
                                                            placeholder={'Enter Email ID'}
                                                            type={"email"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'mobileNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FromikPhoneNumberComponent
                                                            label={'Mobile Number'}
                                                            placeholder={'Mobile Number*'}
                                                            maxLength={14}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'secondaryMobileNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FromikPhoneNumberComponent
                                                            label={'Secondary Mobile Number'}
                                                            placeholder={'Secondary Mobile Number'}
                                                            maxLength={14}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                </>
                                <Grid container spacing={2}>
                                    {
                                        !values.isSameAsAdmin &&
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'faxNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Fax Number'}
                                                            placeholder={'Enter Fax Number'}
                                                            type={"text"}
                                                            maxLength={10}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    }
                                </Grid>
                                <div className="horizontal-line"/>
                                <div className={'form-sub-title mrg-top-20 mrg-bottom-20'}>Account Executive</div>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Field name={'accountExecutiveId'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'Account Executive'}
                                                        required={true}
                                                        data={accountExecutiveList}
                                                        valueKey={"id"}
                                                        placeholder={'Select Account Executive'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option?.firstName) || '')}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                            </div>
                            <div className={"form-action-section-edit"}>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"secondary"}
                                    type={"button"}
                                    variant={"outlined"}
                                    disabled={isSubmitting}
                                    handleClick={() => {
                                        onEditCancel();
                                    }}
                                    fullWidth={false}
                                >
                                    Cancel
                                </ButtonComponent>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"primary"}
                                    type={"submit"}
                                    variant={"contained"}
                                    disabled={!(isValid && dirty)}
                                    fullWidth={false}
                                    isLoading={isSubmitting}
                                >
                                    Save
                                </ButtonComponent>
                            </div>
                        </Form>
                    )
                }
                }
            </Formik>
        </div>
    );

};

export default PrimaryContactDetailsEditComponent;
